import React from "react";
import { motion } from "framer-motion";
import { Briefcase, FileText, Boxes, ShieldCheck, Gauge, Lock, Loader2, ExternalLink } from "lucide-react";
import { Panel, Btn, Overline } from "../ui";
import { GaugeRing } from "../GaugeRing";
import { useI18n, pickLang } from "../../lib/i18n";

function Stat({ label, value, tone = "text-slate-100" }) {
  return (
    <div className="rounded-lg border border-edge well px-4 py-3">
      <Overline>{label}</Overline>
      <p className={`mt-1 font-heading text-2xl font-light tabular ${tone}`}>{value}</p>
    </div>
  );
}

export default function MatterWorkspace({ matters, matterId, setMatterId, detail, loading, onReport, onOpenExport }) {
  const { t, lang } = useI18n();
  const d = detail;

  return (
    <div className="space-y-6" data-testid="screen-matter">
      <div>
        <Overline>{t("mw.overline")}</Overline>
        <h1 className="mt-1.5 font-heading text-3xl font-light tracking-tighter text-slate-100">{t("mw.title")}</h1>
        <p className="mt-2 text-sm text-slate-400">{t("mw.sub")}</p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="matter-switcher">
        {matters.map((m) => (
          <button
            key={m.matter_id}
            onClick={() => setMatterId(m.matter_id)}
            data-testid={`mw-tab-${m.matter_id}`}
            className={`rounded-full border px-4 py-2 text-[12px] transition-colors duration-200 ${
              m.matter_id === matterId
                ? "border-aegis-purple/50 bg-aegis-purple/10 text-aegis-purple"
                : "border-edge raise text-slate-400 hover:text-slate-200"
            }`}
          >
            <span className="font-mono text-[11px]">{m.matter_id}</span>
            <span className="ml-2">{pickLang(m, "name", lang)}</span>
          </button>
        ))}
      </div>

      {loading && !d && (
        <div className="flex items-center gap-3 py-16 text-sm text-slate-400">
          <Loader2 size={18} className="animate-spin text-aegis-cyan" /> {t("mw.loading")}
        </div>
      )}

      {d && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
            <div className="xl:col-span-8">
              <Panel icon={Briefcase} title={pickLang(d, "name", lang)} subtitle={d.matter_id} accent="purple" testid="matter-header">
                <div className="space-y-3">
                  {[
                    [t("mw.authority"), pickLang(d, "authority", lang)],
                    [t("mw.officer"), d.lead_officer],
                    [t("mw.clearance"), `${d.clearance} · ${d.status}`],
                    [t("mw.opened"), d.opened_at],
                  ].map(([k, v]) => (
                    <div key={k} className="flex flex-wrap gap-2 border-b border-edge pb-2 last:border-0">
                      <span className="w-40 shrink-0 text-[11px] uppercase tracking-overline text-slate-400">{k}</span>
                      <span className="text-[13px] text-slate-200">{v}</span>
                    </div>
                  ))}
                  <div className="flex flex-wrap gap-2 pt-1">
                    <span
                      className={`rounded-md border px-2.5 py-1 font-mono text-[11px] ${
                        d.legal_hold
                          ? "border-aegis-amber/40 bg-aegis-amber/10 text-aegis-amber"
                          : "border-edge raise text-slate-400"
                      }`}
                      data-testid="matter-legal-hold"
                    >
                      {t("seal.legalhold")}: {d.legal_hold ? "ACTIVE" : "NOT SET"}
                    </span>
                  </div>
                </div>
                <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <Stat label={t("mw.evidence")} value={d.evidence_count} tone="text-aegis-cyan" />
                  <Stat label={t("mw.sealed")} value={d.sealed_count} tone="text-aegis-emerald" />
                  <Stat label={t("mw.attestations")} value={d.attestations} tone="text-aegis-purple" />
                  <Stat label={t("mw.maxthreat")} value={d.max_threat} tone="text-aegis-red" />
                </div>
                <div className="mt-5 flex flex-wrap gap-2">
                  <Btn variant="cyan" onClick={() => onReport(d.matter_id)} testid="mw-generate-report-btn">
                    <FileText size={15} /> {t("mw.generate")}
                  </Btn>
                  <Btn variant="ghost" onClick={onOpenExport} testid="mw-open-export-btn">
                    <ExternalLink size={15} /> {t("mw.exports")}
                  </Btn>
                </div>
              </Panel>
            </div>

            <div className="xl:col-span-4">
              <Panel icon={Gauge} title={t("mw.custodyScore")} subtitle={t("mw.custodyScoreSub")} accent="emerald" testid="matter-gauge">
                <div className="flex justify-center py-2">
                  {d.evidence_count === 0 ? (
                    <div
                      className="flex h-[172px] w-[172px] flex-col items-center justify-center rounded-full border border-dashed border-edgeStrong"
                      data-testid="matter-custody-gauge-empty"
                    >
                      <span className="font-heading text-4xl font-light text-slate-400">—</span>
                      <span className="mt-1 px-6 text-center text-[11px] text-slate-400">{t("mw.noEvidence")}</span>
                    </div>
                  ) : (
                    <GaugeRing
                      value={Math.round((d.sealed_count / d.evidence_count) * 100)}
                      label={t("mw.sealedRatio")}
                      sublabel="SEALED"
                      tone="emerald"
                      size={172}
                      testid="matter-custody-gauge"
                    />
                  )}
                </div>
              </Panel>
            </div>
          </div>

          <Panel icon={Lock} title={t("mw.evidenceList")} subtitle={t("seal.evidenceCount", { n: d.evidence_count, s: d.sealed_count })} accent="cyan" testid="matter-evidence">
            {d.evidence.length === 0 ? (
              <p className="py-8 text-center text-sm text-slate-400">{t("mw.noEvidence")}</p>
            ) : (
              <div className="space-y-2">
                {d.evidence.map((e) => (
                  <div
                    key={e.evidence_id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-edge raise px-4 py-3"
                    data-testid={`mw-evidence-${e.evidence_id}`}
                  >
                    <div className="min-w-0">
                      <p className="font-mono text-[12px] text-slate-100">{e.evidence_id}</p>
                      <p className="truncate text-[12px] text-slate-400">
                        {e.filename} · {e.classification} · {e.threat_score}/100
                      </p>
                      <p className="mt-0.5 break-all font-mono text-[10px] text-slate-400">{e.sha256}</p>
                    </div>
                    <span
                      className={`shrink-0 font-mono text-[11px] ${e.sealed ? "text-aegis-emerald" : "text-aegis-amber"}`}
                    >
                      {e.sealed ? `#${e.seal_block}` : t("seal.unsealed")}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Panel>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <Panel icon={Boxes} title={t("mw.custodyLog")} subtitle={t("mw.custodyLogSub")} accent="emerald" testid="matter-custody-log">
              {d.custody_log.length === 0 ? (
                <p className="py-8 text-center text-sm text-slate-400">{t("mw.noCustody")}</p>
              ) : (
                <div className="space-y-2">
                  {d.custody_log.map((c, i) => (
                    <div key={c.index ?? c.created_at ?? i} className="rounded-lg border border-edge raise px-4 py-3" data-testid={`mw-custody-${i}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="font-mono text-[12px] text-aegis-emerald">#{c.block_index}</span>
                        <span className="font-mono text-[10px] text-slate-400">{c.sealed_at}</span>
                      </div>
                      <p className="mt-1 truncate text-[12px] text-slate-300">
                        {c.evidence_id || c.filename || "—"} · {c.operator}
                      </p>
                      <p className="mt-0.5 break-all font-mono text-[10px] text-slate-400">{c.sha256}</p>
                    </div>
                  ))}
                </div>
              )}
            </Panel>

            <Panel icon={ShieldCheck} title={t("mw.exportHistory")} subtitle={t("mw.exportHistorySub")} accent="amber" testid="matter-reports">
              {d.reports.length === 0 ? (
                <p className="py-8 text-center text-sm text-slate-400">{t("mw.noReports")}</p>
              ) : (
                <div className="space-y-2">
                  {d.reports.map((r) => (
                    <div key={r.report_id} className="rounded-lg border border-edge raise px-4 py-3" data-testid={`mw-report-${r.report_id}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="font-mono text-[12px] text-aegis-amber">{r.report_id}</span>
                        <span className="font-mono text-[10px] text-slate-400">{r.generated_at}</span>
                      </div>
                      <p className="mt-1 text-[12px] text-slate-300">{pickLang(r, "headline", lang)}</p>
                      <p className="mt-1 font-mono text-[10px] text-slate-400">
                        {r.risk_posture} · readiness {r.stats?.readiness_score}%
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </Panel>
          </div>
        </motion.div>
      )}
    </div>
  );
}
